import { ArrowRight, Shield, Lock, Clock } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-eval-suite-1/artifacts/qdv0hpzq_IMG_5455.jpeg";

const TESTIMONIALS = [
  {
    quote: "I went in thinking it was just another quiz. I came out unable to unsee what it showed me. The accuracy was unsettling — in the best way.",
    name: "T. Williams",
    title: "Senior Director, Federal Agency",
    detail: "The Performer / Keeper",
  },
  {
    quote: "I've done personality tests, coaching sessions, even therapy. Nothing has named the pattern this clearly. It felt like someone finally held up the right mirror.",
    name: "R. Carter",
    title: "VP of Operations, Tech",
    detail: "The Prover / Stabilizer",
  },
];

export default function IntroScreen({ onStart }) {
  return (
    <div data-testid="intro-screen" className="min-h-screen bg-[#054452] flex flex-col">
      {/* Header */}
      <header className="px-6 py-6 sm:px-10 sm:py-8 flex items-center justify-center">
        <div className="bg-[#F8D4CE] rounded-lg p-1.5 shadow-lg" style={{ boxShadow: "0 4px 20px rgba(248, 212, 206, 0.2)" }}>
          <img
            src={LOGO_URL}
            alt="Nijah Almond"
            className="h-10 sm:h-11 object-contain rounded"
            data-testid="brand-logo"
          />
        </div>
      </header>

      {/* Hero Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 pb-6">
        <div className="max-w-[680px] w-full text-center">
          {/* Brand Label */}
          <p className="font-body text-[10px] sm:text-xs tracking-[0.25em] uppercase text-[#0C6474] mb-8 sm:mb-10 animate-fade-in-up">
            Nijah Almond Coaching & Consultancy
          </p>

          {/* Main Headline */}
          <h1
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-[#F8D4CE] leading-snug mb-2 animate-fade-in-up-delay-1"
            data-testid="hero-headline"
          >
            You don't need more clarity.
          </h1>
          <h1
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-snug mb-8 animate-fade-in-up-delay-2"
            data-testid="hero-headline-emphasis"
          >
            <em className="text-[#E66E6D]">You need to see who you've been operating as.</em>
          </h1>

          {/* Gold Divider */}
          <div className="w-10 h-[1px] bg-[#F3B21A] mx-auto mb-8 animate-fade-in-up-delay-2" />

          {/* Subheadline */}
          <p
            className="font-body text-sm sm:text-base text-[#F8D4CE] font-light max-w-[540px] mx-auto mb-8 leading-relaxed animate-fade-in-up-delay-2"
            data-testid="hero-subheadline"
          >
            A 3-minute diagnostic that reveals the identity pattern driving
            your decisions, behaviors, and results — and why it no longer fits.
          </p>

          {/* Trust Points */}
          <div className="flex flex-col items-center gap-2.5 mb-8 animate-fade-in-up-delay-3">
            {[
              { id: "trust-time", icon: Clock, text: "Takes less than 3 minutes" },
              { id: "trust-private", icon: Lock, text: "100% private & confidential" },
              { id: "trust-frameworks", icon: Shield, text: "Built from real coaching frameworks" },
            ].map((item) => (
              <span key={item.id} className="flex items-center gap-3 text-[#F8D4CE] text-xs sm:text-sm font-body font-light">
                <item.icon size={14} className="text-[#F3B21A] flex-shrink-0" />
                {item.text}
              </span>
            ))}
          </div>

          {/* CTA Button */}
          <button
            onClick={onStart}
            data-testid="begin-analyzer-btn"
            className="inline-flex items-center justify-center gap-3 bg-[#F3B21A] text-[#054452] w-full max-w-[400px] py-4 font-body font-medium text-sm tracking-[0.15em] uppercase hover:bg-[#E66E6D] hover:text-white transition-colors duration-300 animate-fade-in-up-delay-3 active:scale-[0.98]"
          >
            Begin the Analyzer
            <ArrowRight size={16} />
          </button>

          {/* Creator attribution */}
          <p className="font-body text-xs text-[#0C6474] mt-6 animate-fade-in-up-delay-4">
            Created by <span className="text-[#F8D4CE]">Nijah Almond</span> — Purpose Coach & Brand Strategist
          </p>
        </div>

        {/* Testimonials */}
        <div className="max-w-[720px] w-full mt-12 sm:mt-16 animate-fade-in-up-delay-4">
          {/* Credibility counter */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex -space-x-2">
              {["av1","av2","av3","av4","av5"].map((id) => (
                <div key={id} className="w-6 h-6 rounded-full border-2 border-[#054452] bg-[#0C6474]" />
              ))}
            </div>
            <p className="font-body text-xs sm:text-sm text-[#F8D4CE]">
              Taken by <span className="text-[#F3B21A] font-medium">500+</span> women in leadership
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {TESTIMONIALS.map((t) => (
              <div
                key={t.name}
                data-testid={`testimonial-${t.name}`}
                className="border border-[#0C6474] p-5 sm:p-6 text-left"
              >
                <p className="font-display text-sm sm:text-base italic text-[#F8D4CE] leading-relaxed mb-4">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F3B21A]" />
                  <p className="font-body text-xs text-[#F8D4CE] font-medium">
                    {t.name}
                  </p>
                  <span className="text-[#0C6474] text-xs">·</span>
                  <p className="font-body text-xs text-[#0C6474]">
                    {t.title}
                  </p>
                </div>
                <p className="font-body text-[10px] text-[#E66E6D] mt-1 ml-4">
                  {t.detail}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Privacy Footer */}
        <div className="mt-10 sm:mt-12 flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-[#0C6474] text-[10px] sm:text-xs animate-fade-in-up-delay-4">
          <span className="flex items-center gap-1.5">
            <Lock size={10} />
            SSL Encrypted
          </span>
          <span className="flex items-center gap-1.5">
            <Shield size={10} />
            No data sold
          </span>
          <span>Results are yours alone</span>
        </div>
      </main>
    </div>
  );
}
